/**
 * Diya coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_diwali`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diyaColoringPagesPack: ToilePack = {
    slug: 'diya-coloring-pages',
    category: 'faith',
    name: 'Diya coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'single', label: 'Single diya', subject: 'one large diya with a tall flame, petal shapes on its body and a few stars around it'},
        {key: 'thali', label: 'Puja thali', subject: 'a round puja thali holding three diyas, a small bell, flowers and a kumkum bowl'},
        {key: 'pattern', label: 'Diya pattern', subject: 'a page of nine small diyas in rows, each with a different simple pattern of dots, zigzags or leaves'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and easy', style: 'drawn for little children with extra-thick uniform outlines and a few big simple shapes', palette: 'black ink only', framing: 'the full page used, the drawing large and centered with a white margin'},
        {key: 'mandala', label: 'Mandala', style: 'drawn for older children with fine mandala bands of petals, dots and scallops inside each shape', palette: 'black ink only', framing: 'the full page used, the drawing centered with a white margin'},
        {key: 'cute', label: 'Cute faces', style: 'drawn in a cute cartoon manner, each lamp given a smiling face with round eyes and a happy little flame', palette: 'black ink only', framing: 'the full page used, the drawing centered with a white margin'},
    ],
    directions: ['peacock diya', 'with rangoli', 'for toddlers'],
    directionHint: 'try: peacock diya',
    seo: {
        title: 'Diya coloring pages: single diya, puja thali, pattern',
        metaDescription: 'Nine printable diya coloring pages for children: one lamp, a puja thali and a pattern page, in bold, mandala and cute styles.',
        h1: 'Diya coloring pages: single diya, puja thali, diya pattern',
        tagline: 'Nine clean outlines to print and colour, three scenes each in a bold, a mandala and a cute style.',
        intro: 'Printable coloring pages of diyas, the clay lamps lit at Diwali and Karthigai Deepam, for children to colour at home or in class before the festival. Each is a clean black outline with large areas to fill: one large diya, a puja thali with three lamps, and a page of nine small diyas with simple patterns. The bold style suits little children, the mandala style older ones, and the cute style has smiling lamps.',
        useCases: [
            'a colouring page for a child at home',
            'a classroom activity before the festival',
            'a quiet activity at a family puja',
            'a sheet for toddlers to colour',
        ],
        faq: [
            {q: 'Which style is for which age?', a: 'The bold style has extra-thick outlines for toddlers, the mandala style has finer bands of petals and dots for older children, and the cute style has rounded lamps with smiling faces.'},
            {q: 'Can the pages be printed?', a: 'Yes. Each is a black line drawing on pure white with no grey tones, so it prints clearly in black ink.'},
            {q: 'What is on the puja thali page?', a: 'A round thali holding three diyas, a small bell, flowers and a kumkum bowl.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. The direction field takes a change such as a peacock diya or with rangoli. A direction you type is a request, not a promised result.'},
        ],
    },
}
